// Pure helpers for the Configuration Service settings (§211–§214). No GATT
// here — this module is unit-testable in full, and device.ts owns the reads
// and writes that use it.
import { SoluuError } from './errors.js';
/**
 * Nominal ranges, for advisory UI hints ONLY. **Nothing in the write path
 * consults these**, and nothing ever should.
 *
 * A tester must be able to send an out-of-nominal value and observe what the
 * firmware does with it. Firmware today accepts such a value, persists it to
 * NV storage, and drives the hardware with it (D-006) — a harness that
 * clamped to these ranges would make that defect invisible, which is the
 * whole reason the stage exists.
 *
 * `volumeLevel` is 0–100 (percent), per D-002 / W-010 — NOT the GATT
 * document's 1–10. The firmware's PWM path is a 0–100 duty percentage and
 * its default is 100, so 0–100 is the coherent standard.
 */
export const NOMINAL_RANGES = {
    pulseIntensity: [0, 8],
    volumeLevel: [0, 100],
};
/**
 * The **only** validation applied to a settings write: the value has to fit
 * in a uint8, because that is what the characteristic is. Structural limit
 * that protects the device, nothing more.
 *
 * Never clamps. An out-of-nominal but in-range value (e.g. intensity 123)
 * passes through untouched and reaches the device exactly as typed.
 */
export function asUint8(value, label = 'Value') {
    if (!Number.isInteger(value)) {
        throw new SoluuError('GATT_ERROR', `${label} must be an integer (got ${value})`);
    }
    if (value < 0 || value > 255) {
        throw new SoluuError('GATT_ERROR', `${label} must be 0–255 (got ${value})`);
    }
    return value;
}
/** Read the first byte of a settings characteristic value. */
export function uint8FromView(view) {
    if (view.byteLength === 0) {
        throw new RangeError('Cannot decode a uint8 from an empty payload');
    }
    return view.getUint8(0);
}
/** Firmware convention for the two enable flags: 0 = off, non-zero = on. */
export function boolFromUint8(value) {
    return value !== 0;
}
/**
 * Decode the four settings from whatever was successfully read. Each input
 * is independently nullable so one missing characteristic or one failed read
 * degrades a single field.
 */
export function decodeConfig(views) {
    return {
        pulseIntensity: views.pulseIntensity ? uint8FromView(views.pulseIntensity) : null,
        volumeEnabled: views.volumeEnabled
            ? boolFromUint8(uint8FromView(views.volumeEnabled))
            : null,
        volumeLevel: views.volumeLevel ? uint8FromView(views.volumeLevel) : null,
        vibrationEnabled: views.vibrationEnabled
            ? boolFromUint8(uint8FromView(views.vibrationEnabled))
            : null,
    };
}
//# sourceMappingURL=config.js.map